/**
 * The `table` variable source: lets an author point an email template at a
 * database table and expose its columns as `$(column)` variables.
 *
 * For alerts dispatched from code rather than by a form submission, where the
 * sender reads a row and passes it in. The JSON source (a pasted example
 * object) covers the same ground by hand; this one cannot drift from the real
 * schema, because it reads it.
 *
 * Both calls go through RPCs that are admin-gated server-side and scoped to
 * base tables in the `public` schema — see the schema browser section of
 * `supabase/migrations/20261005181236_pho_1221.sql` for what that does and does
 * not expose. Nothing here reads a row: names and types only.
 */

import DBOperationsComb from "src/utils/dbOperationsComb";
import Exception from "src/utils/exception";
import Helper from "src/utils/helper";

/**
 * Every base table in the public schema.
 *
 * @returns {Promise<{tables: Array<string>, error: string|null}>}
 */
async function listTables() {
  try {
    const res = await DBOperationsComb.listPublicTables();
    if (res?.error) return { tables: [], error: res.error };
    return {
      tables: (res?.data ?? []).map((row) => row.table_name),
      error: null
    };
  } catch (error) {
    Exception.catchException(error);
    return { tables: [], error: error.message };
  }
}

/**
 * The columns of one base table, in declaration order.
 *
 * @param {string} tableName
 * @returns {Promise<{columns: Array<{name: string, dataType: string}>, error: string|null}>}
 */
async function listTableColumns(tableName) {
  if (!Helper.isStringValueExist(tableName)) {
    return { columns: [], error: "Table name is required" };
  }
  try {
    const res = await DBOperationsComb.listPublicTableColumns({ tableName });
    if (res?.error) return { columns: [], error: res.error };
    return {
      columns: (res?.data ?? []).map((row) => ({
        name: row.column_name,
        dataType: row.data_type
      })),
      error: null
    };
  } catch (error) {
    Exception.catchException(error);
    return { columns: [], error: error.message };
  }
}

export default {
  listTables,
  listTableColumns
};
